<template>
  <layout>
    <div
      class="space-y-1 pb-4 pt-6 px-4 sm:p-6 bg-white flex-col space-y-2 items-center justify-between border-b border-b-gray-200"
    >
      <ArrowNarrowLeft
        class="w-6 sm:w-0 sm:hidden -ml-0.5 text-gray-900"
        @click="handleBack"
      />
      <div class="flex items-center justify-between">
        <h1 class="text-gray-900 text-xl font-medium">
          {{ t("admin.settings.location_settings.title") }}
        </h1>
      </div>
    </div>

    <div class="dynamic-height overflow-auto bg-white p-6">
      <div class="max-w-xl flex flex-col gap-8">
        <Errors class="mb-6" :errors="form.errors" />

        <DynamicInputComponent
          v-for="field in fields"
          :key="field.key"
          :modelValue="form[field.key]"
          @update:modelValue="onInput"
          :field="field"
          :form="form"
          :disabled="checkFieldDisabled(field)"
          :horizontal="!isMobile"
          debounce
        />

        <DynamicInputComponent
          v-for="field in customFields"
          :key="field.key"
          :modelValue="customForm[field.key]"
          @update:modelValue="onCustomFieldsInput"
          @delete="handleConfirmDeleteField"
          :field="field"
          :form="customForm"
          :disabled="checkFieldDisabled(field)"
          :horizontal="!isMobile"
          :show-delete-icon="canEditTenantFields"
          debounce
        />

        <div v-if="canEditTenantFields">
          <secondary-button
            @click="toggleIsAddingNewField"
            class="flex grow-0"
            :label="
              t('admin.settings.location_settings.tenant_fields.add_field')
            "
          />
        </div>
      </div>
    </div>
    <NewTenantField
      :open="isAddingNewField"
      :saving="isSavingNewField"
      @save="handleSaveTenantField"
      @close="toggleIsAddingNewField"
    />
    <ConfirmationModal
      :open="!!fieldBeingDeleted"
      :disabled="isDeletingField"
      @confirm="handleDeleteTenantField"
      @close="fieldBeingDeleted = null"
    />
  </layout>
</template>

<script setup>
import PersistentLayout from "@/Layouts/PersistentLayout.vue";
import Layout from "@/Tailwind/Layouts/TenantSettings.vue";
import { computed, inject, ref } from "vue";
import { router } from "@inertiajs/vue3";
import route from "ziggy-js";
import { useI18n } from "vue-i18n";
import ArrowNarrowLeft from "@/Tailwind/Icon/ArrowNarrowLeft.vue";
import DynamicInputComponent from "@/Tailwind/Components/Settings/DynamicInputComponent.vue";
import Errors from "@/Tailwind/Components/Errors.vue";
import BuildingSeven from "@/Tailwind/Icon/BuildingSeven";
import { useFormSetup } from "@/Tailwind/Mixins/useFormSetup";
import NewTenantField from "@/Tailwind/Components/NewTenantField.vue";
import SecondaryButton from "@/Tailwind/Components/Buttons/SecondaryButton.vue";
import ConfirmationModal from "@/Tailwind/Components/ConfirmationModal.vue";
import { useBreakpoints } from "@/Tailwind/Mixins/useBreakpoints";

const { t } = useI18n();
const { hasPermission, permissions } = inject("permissions");
const { mapDetailsToForm } = useFormSetup();

const { isMobile } = useBreakpoints();

defineOptions({ layout: PersistentLayout });

const isDeletingField = ref(false);
const fieldBeingDeleted = ref();
const handleConfirmDeleteField = (field) => {
  fieldBeingDeleted.value = field;
};

const canEditTenantFields = computed(() => {
  return hasPermission(permissions.updateTenant);
});
const isSavingNewField = ref(false);
const isAddingNewField = ref(false);
const toggleIsAddingNewField = () =>
  (isAddingNewField.value = !isAddingNewField.value);

const handleSaveTenantField = (form) => {
  if (!hasPermission(permissions.updateTenant)) {
    return;
  }

  return form.post(route("settings.location.fields.store", [props.location]), {
    onStart: () => (isSavingNewField.value = true),
    onError: () => (isSavingNewField.value = false),
    onSuccess: () => {
      isAddingNewField.value = false;
      isSavingNewField.value = false;

      customForm = mapDetailsToForm(
        customFields.value,
        customFieldsDetails.value,
        null,
        true
      );
    },
  });
};

const props = defineProps({
  location: Object,
});

const customFields = computed(() =>
  props.location.fields.map((field) => ({
    key: field.name,
    id: field.id,
    title: field.name,
    type: "text",
  }))
);

const customFieldsDetails = computed(() =>
  Object.assign(
    {},
    ...props.location.fields.map((field) => ({
      [field.name]: field.value,
    }))
  )
);

const fields = computed(() => [
  {
    key: "logo",
    title: t("admin.settings.location_settings.logo.title"),
    type: "image",
    value: props.location?.logo_url,
    placeholder: BuildingSeven,
  },
  {
    key: "name",
    title: t("admin.settings.location_settings.name.title"),
    placeholder: t("admin.settings.location_settings.name.placeholder"),
    type: "text",
  },
  {
    key: "address",
    title: t("admin.settings.location_settings.address.title"),
    placeholder: t("admin.settings.location_settings.address.placeholder"),
    type: "text",
  },
  {
    key: "city_postal",
    title: t("admin.settings.location_settings.city_postal.title"),
    type: "composite",
    fields: [
      {
        key: "city",
        type: "text",
        placeholder: t("admin.settings.location_settings.city_postal.city"),
      },
      {
        key: "postal_code",
        type: "text",
        placeholder: t(
          "admin.settings.location_settings.city_postal.postal_code"
        ),
      },
    ],
  },
  {
    key: "country",
    title: t("admin.settings.location_settings.country.title"),
    placeholder: t("admin.settings.location_settings.country.placeholder"),
    type: "text",
  },
  {
    key: "vat",
    title: t("admin.settings.location_settings.vat.title"),
    placeholder: t("admin.settings.location_settings.vat.placeholder"),
    type: "text",
  },
  {
    key: "contact_person",
    title: t("admin.settings.location_settings.contact_person.title"),
    placeholder: t(
      "admin.settings.location_settings.contact_person.placeholder"
    ),
    type: "text",
  },
]);

let form = mapDetailsToForm(fields.value, props.location, null, true);
let customForm = mapDetailsToForm(
  customFields.value,
  customFieldsDetails.value,
  null,
  true
);

function handleUpdateTenantField(id, value) {
  if (!hasPermission(permissions.updateTenant)) {
    return;
  }

  router.patch(route("settings.location.fields.update", [props.location, id]), {
    value,
  });
}

function handleDeleteTenantField() {
  if (!hasPermission(permissions.updateTenant)) {
    return;
  }

  router.delete(
    route("settings.location.fields.destroy", [
      props.location,
      fieldBeingDeleted.value.id,
    ]),
    {
      onStart: () => (isDeletingField.value = true),
      onFinish: () => {
        fieldBeingDeleted.value = null;
        isDeletingField.value = false;
      },
    }
  );
}

function handleUpdateLocation() {
  if (!hasPermission(permissions.updateTenant)) {
    return;
  }

  form
    .transform((data) => ({
      ...data,
      // We need to act like a post request, but we want to use the patch method
      // This is so we can send the image to the server
      _method: "patch",
    }))
    .post(route("settings.location.update", [props.location]));
}

const checkFieldDisabled = (field) => {
  if (!hasPermission(permissions.updateTenant)) {
    return true;
  }

  if (typeof field.disabled === "function" && field.disabled(form)) {
    return true;
  }

  if (typeof field.disabled === "boolean" && field.disabled) {
    return true;
  }

  return false;
};

// We can't use vModel because the dynamic input component will
// emit different fields if it is a composite field
const onInput = (field, value) => {
  form[field.key] = value;
  handleUpdateLocation();
};

const onCustomFieldsInput = (field, value) => {
  customForm[field.key] = value;
  handleUpdateTenantField(field.id, value);
};

function handleBack() {
  router.visit(route("settings.tenant", [props.location]));
}
</script>

<style lang="scss" scoped>
.dynamic-height {
  height: calc(100vh - 64px - 87px);
}
</style>
